import { useState, useEffect, useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useCookies } from 'react-cookie';
import useAuthentication from '@hooks/useAuthentication';
import useSessionStorage from '@hooks/useSessionStorage';
import { addLocalStorageHotelsToFavoritesUponLogin } from '@helpers/addHotelToFavorites';
import { myFavoritesCreateAccount } from '@events/myFavorites';
import { accessAllFavorites } from '@utils/services';
import { store } from '@context/store';

type UseLoginAndCartReturn = {
  isLoginMenuOpen: boolean;
  setIsLoginMenuOpen: (value: boolean) => void;
  isSignUpClicked: boolean;
  setIsSignUpClicked: (value: boolean) => void;
  buttonText: string;
  handleAuthClick: (isLogout?: boolean) => Promise<void>;
  isUserSignedIn: boolean;
  showCookiesConsentBanner: boolean;
  isLoginButtonClicked: boolean;
  setIsLoginButtonClicked: (value: boolean) => void;
};

export default function useLoginAndCart(): UseLoginAndCartReturn {
  const router = useRouter();
  const session = useSession();
  const { oauthLogin, logoutUser } = useAuthentication();
  const { state } = useContext(store);
  const { showCookiesConsentBanner } = state;
  const [cookies, setCookie] = useCookies(['userInformation']);
  const { getItem } = useSessionStorage();
  const [isLoginMenuOpen, setIsLoginMenuOpen] = useState(false);
  const [isLoginButtonClicked, setIsLoginButtonClicked] = useState(false);
  const [isSignUpClicked, setIsSignUpClicked] = useState(false);
  const [isUserSignedIn, setIsUserSignedIn] = useState(false);
  const [buttonText, setButtonText] = useState('Log in / Sign up');

  const handleAuthClick = useCallback(
    async (isLogout: boolean = false) => {
      if (isLogout) {
        await logoutUser();
        setButtonText('Log in / Sign up');
        setIsUserSignedIn(false);

        if (
          router.pathname.includes('/hotels/') ||
          router.pathname.includes('/checkout-confirmation') ||
          router.pathname.includes('/my-favorites')
        ) {
          router.reload();
        }
      } else {
        setIsLoginButtonClicked(true);
        setIsLoginMenuOpen(false);
      }
    },
    [logoutUser, router],
  );

  useEffect(() => {
    const isUserAuthenticated =
      session.status && session.status !== 'unauthenticated' && session.status !== 'loading';
    if (isUserAuthenticated && !isUserSignedIn && !cookies.userInformation?.email) {
      (async () => {
        try {
          const cartId = getItem('cartId');
          const userInfo = await oauthLogin(session, cartId);

          if (userInfo === 'registered_with_email') {
            setButtonText('Log in / Sign up');
            // Assuming toggleErrorPopup needs a dispatch, omitted here for simplicity
            return;
          }

          if (userInfo && userInfo !== 'error') {
            const userWithFavorites = {
              ...userInfo,
              favorites: Object.keys(await accessAllFavorites(userInfo)) || [],
            };

            setCookie('userInformation', userWithFavorites, { path: '/' });
            setButtonText('Log out');
            setIsUserSignedIn(true);

            if (window.Iterable) {
              const iterableEmail =
                getItem('iterableEmail') || `${getItem('sessionId')}@placeholder.email`;
              await window.Iterable.updateUserEmail(userInfo.email, iterableEmail, userInfo);
            }

            if (window.location.href.includes('my-favorites')) {
              myFavoritesCreateAccount();
              window.location.reload();
            }

            await addLocalStorageHotelsToFavoritesUponLogin(userWithFavorites, getItem);
          }
        } catch (error) {
          setButtonText('Log in / Sign up');
          setIsUserSignedIn(false);
        }
      })();
    } else if (cookies.userInformation?.email && cookies.userInformation?.id) {
      setButtonText('Log out');
      setIsUserSignedIn(true);
    } else if (cookies.userInformation === null) {
      setButtonText('Log in / Sign up');
      setIsUserSignedIn(false);
    }
  }, [session, cookies, setCookie, getItem, isUserSignedIn, oauthLogin]);

  useEffect(() => {
    const cartId = getItem('cartId');
    const cartItems = parseInt(getItem('cartNumberOfItems') ?? '0', 10);
    if (cartId && isUserSignedIn && cartItems > 0) {
      router.push('/users/guest-billing-detail');
    }
  }, [getItem, isUserSignedIn, router]);

  return {
    isLoginMenuOpen,
    setIsLoginMenuOpen,
    isSignUpClicked,
    setIsSignUpClicked,
    buttonText,
    handleAuthClick,
    isUserSignedIn,
    showCookiesConsentBanner,
    isLoginButtonClicked,
    setIsLoginButtonClicked,
  };
}
